import { gql } from './client';
import { emit, withExpected, postToS3, firstOrThrow, type PresignedPost } from './events';

export interface PlanningDocument {
  id: string;
  name: string;
  category?: string;
  kind?: string;
  filename: string;
  contentType?: string;
  size?: number;
  s3Key: string;
  scanStatus?: 'Pending' | 'Clean' | 'Infected' | 'Failed' | null;
  scanDetail?: string | null;
  createdAt: string;
  updatedAt: string;
  version?: string;
}

const FIELDS = `id name category kind filename contentType size s3Key scanStatus scanDetail createdAt updatedAt version`;

export async function listPlanningDocuments(): Promise<PlanningDocument[]> {
  const data = await gql<{ elevationsPlanningDocuments: PlanningDocument[] }>(
    `query ListPlanningDocuments { elevationsPlanningDocuments { ${FIELDS} } }`,
  );
  return data.elevationsPlanningDocuments ?? [];
}

export async function getPlanningDocumentUploadUrl(filename: string, contentType: string): Promise<PresignedPost> {
  const data = await gql<{ elevationsPlanningDocumentUploadUrl: PresignedPost[] }>(
    `query GetPlanningDocumentUploadUrl($filename: String!, $contentType: String!) {
      elevationsPlanningDocumentUploadUrl(filename: $filename, contentType: $contentType) { url fields s3Key }
    }`,
    { filename, contentType },
  );
  return firstOrThrow(data.elevationsPlanningDocumentUploadUrl, 'Failed to get an upload URL');
}

export async function getPlanningDocumentDownloadUrl(documentId: string): Promise<string> {
  const data = await gql<{ elevationsPlanningDocumentDownloadUrl: { url: string }[] }>(
    `query GetPlanningDocumentDownloadUrl($documentId: ID!) {
      elevationsPlanningDocumentDownloadUrl(documentId: $documentId) { url }
    }`,
    { documentId },
  );
  return firstOrThrow(data.elevationsPlanningDocumentDownloadUrl, 'Failed to get a download URL').url;
}

export function uploadPlanningDocumentToS3(url: string, fields: Record<string, string>, file: File): Promise<void> {
  return postToS3(url, fields, file);
}

// The server assigns the id; callers find the new row afterwards by its s3Key.
export function emitCreatePlanningDocument(
  payload: { s3Key: string; filename: string; name: string; category?: string; performedBy?: string },
  connectionId?: string,
): Promise<string> {
  return emit('CreatePlanningDocument', crypto.randomUUID(), payload, connectionId);
}

export function emitUpdatePlanningDocument(
  id: string, patch: { name?: string; category?: string; performedBy?: string },
  connectionId?: string, expectedValues?: Record<string, unknown>,
): Promise<string> {
  return emit('UpdatePlanningDocument', id, withExpected(patch, expectedValues), connectionId);
}

export function emitDeletePlanningDocument(id: string, performedBy?: string, connectionId?: string): Promise<string> {
  return emit('DeletePlanningDocument', id, { performedBy }, connectionId);
}
